import { Box, Button, CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import JsonSchemaForm from 'react-json-schema-form';
import useApi from '../../../hooks/useAPI';
import RadDialog from '../../RadDialog';

const EditProjectModal = ({ isOpen, onClose, onSubmit, project }) => {
  const formRef = useRef(null);
  const { credentials: credentialsApi } = useApi();
  const [credentialOptions, setCredentialOptions] = useState([]);
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || !project) {
      setCredentialsLoaded(false);
      return;
    }
    let cancelled = false;
    setCredentialsLoaded(false);
    (async () => {
      try {
        const response = await credentialsApi.list();
        if (cancelled) return;
        const list = Array.isArray(response.body) ? response.body : [];
        setCredentialOptions(
          list.map((c) => ({ value: c.id, label: c.name }))
        );
      } catch (e) {
        console.error('Failed to load credentials for project edit:', e);
        if (!cancelled) setCredentialOptions([]);
      } finally {
        if (!cancelled) setCredentialsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, project?.id, credentialsApi]);

  // Must accept formData — JsonSchemaForm calls this after validate; do not call
  // formRef.submit() here (that would recurse and can unmount before getData).
  const handleFormSubmit = useCallback(
    async (formData) => {
      await onSubmit({
        id: project.id,
        name: formData.name,
        credentials: formData.credentials || [],
      });
      onClose();
    },
    [onSubmit, onClose, project]
  );

  if (!project) {
    return null;
  }

  const credentialIds = credentialOptions.map((option) => option.value);
  // Ajv rejects `enum: []`; only restrict values when there is at least one option.
  const credentialItems =
    credentialIds.length > 0
      ? { type: 'string', enum: credentialIds }
      : { type: 'string' };

  const schema = {
    type: 'object',
    properties: {
      name: { type: 'string', title: 'Project Name' },
      credentials: {
        type: 'array',
        title: 'Project Credentials',
        items: credentialItems,
        uniqueItems: true,
      },
    },
  };

  const uiSchema = {
    credentials: {
      'ui:widget': 'checkboxes',
      'ui:options': {
        inline: true,
        labels: credentialOptions.reduce((acc, option) => {
          acc[option.value] = option.label;
          return acc;
        }, {}),
      },
    },
  };

  const initialData = {
    name: project.name,
    credentials: project.credentials
      ? project.credentials.map((cred) => cred.id)
      : [],
  };

  return (
    <RadDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Project"
      actions={
        <>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => formRef.current?.submit()}
            color="primary"
            variant="contained"
            disabled={!credentialsLoaded}
          >
            Save
          </Button>
        </>
      }
    >
      {!credentialsLoaded ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <JsonSchemaForm
          ref={formRef}
          schema={schema}
          uiSchema={uiSchema}
          initialData={initialData}
          onSubmit={handleFormSubmit}
          hideSubmitButton={true}
        />
      )}
    </RadDialog>
  );
};

export default EditProjectModal;
